/**
 * Chandelier Exit
 *
 * Trailing stop-loss indicator based on ATR.
 * Long exit = Highest High − ATR × multiplier
 * Short exit = Lowest Low + ATR × multiplier
 *
 * Category: Volatility | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeATR } from "./atr";

export const chandelierIndicator: IndicatorDefinition = {
  id: "chandelier",
  name: "Chandelier Exit",
  shortName: "CE",
  category: "volatility",
  pane: "overlay",
  params: [
    { key: "period", label: "ATR Period", type: "number", default: 22, min: 1, max: 200 },
    { key: "multiplier", label: "Multiplier", type: "number", default: 3, min: 0.5, max: 10, step: 0.1 },
  ],
  plots: [
    { key: "longExit", label: "Long Exit", color: "#4CAF50", style: "line", lineWidth: 2 },
    { key: "shortExit", label: "Short Exit", color: "#F44336", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, multiplier } = params;
    const atr = computeATR(candles, period);
    const longData: { time: number; value: number }[] = [];
    const shortData: { time: number; value: number }[] = [];

    for (let i = period - 1; i < candles.length; i++) {
      if (isNaN(atr[i])) continue;

      let highestHigh = -Infinity, lowestLow = Infinity;
      for (let j = i - period + 1; j <= i; j++) {
        if (candles[j].high > highestHigh) highestHigh = candles[j].high;
        if (candles[j].low < lowestLow) lowestLow = candles[j].low;
      }

      longData.push({ time: candles[i].time, value: highestHigh - multiplier * atr[i] });
      shortData.push({ time: candles[i].time, value: lowestLow + multiplier * atr[i] });
    }

    return [
      { key: "longExit", data: longData },
      { key: "shortExit", data: shortData },
    ];
  },
};
